import { api } from '../api';

export type ResidentStatus = 'ADMITTED' | 'DISCHARGED';
export type ResidentType = 'RESIDENT' | 'OUTPATIENT_A' | 'OUTPATIENT_B';
export type ResidentListSortKey = 'room' | 'bgtScoreAsc' | 'voiceWellnessAsc';
export type AlertType = 'UNUSED' | 'SCORE_DROP' | 'VOICE_UNUSED';

export interface ResidentAlert {
  type: AlertType;
  /** 배지 색은 종류가 아니라 심각도를 따른다 */
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  detail: string;
}

export interface BgtUserScore {
  userId: string;
  date: string;
  hasData: boolean;
  testCount: number;
  correctCount: number;
  avgScore: number | null;
  totalUseTime: number;
  contentScores: Record<string, number>;
  source: 'cache' | 'live' | 'empty';
}

export interface VoiceUserScore {
  voiceUserId: number;
  date: string;
  hasData: boolean;
  testId: number | null;
  hearingScore: number | null;
  cognitiveScore: number | null;
  stressScore: number | null;
  source: 'cache' | 'live' | 'empty';
}

export interface ResidentListItem {
  id: string;
  userId: string | null;
  roomId: string | null;
  /** voicecheck.users.id — 입소자 단위 보이스 매핑 */
  voiceUserId: number | null;
  status: ResidentStatus;
  type: ResidentType;
  admittedAt: string | null;
  user: {
    id: string;
    name: string | null;
    patientCode: string | null;
    birthDate: string | null;
    age: number | null;
  } | null;
  room: { id: string; roomNumber: string; floor: number } | null;
  bgtScore: BgtUserScore | null;
  yesterdayBgtScore: BgtUserScore | null;
  voiceWellnessScore: VoiceUserScore | null;
  usedToday: boolean;
  alerts: ResidentAlert[];
}

export interface PaginatedResidentList {
  items: ResidentListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ListResidentsDashboardQuery {
  page?: number;
  limit?: number;
  status?: ResidentStatus;
  type?: ResidentType;
  roomId?: string;
  floor?: number;
  alert?: boolean;
  unused?: boolean;
  dropped?: boolean;
  orderBy?: ResidentListSortKey;
}

export interface ResidentDto {
  id: string;
  userId: string | null;
  roomId: string | null;
  voiceUserId: number | null;
  status: ResidentStatus;
  type: ResidentType;
  admittedAt: string | null;
  dischargedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    patientCode: string | null;
    birthDate: string | null;
    age: number | null;
  } | null;
  room: { id: string; roomNumber: string; floor: number } | null;
}

export interface PaginatedResidents {
  items: ResidentDto[];
  total: number;
  page: number;
  limit: number;
}

export interface ListResidentsQuery {
  page?: number;
  limit?: number;
  status?: ResidentStatus;
  type?: ResidentType;
  roomId?: string;
  floor?: number;
}

export interface CreateResidentInput {
  userId?: string;
  /**
   * 새 BGT 회원을 함께 만들 때 쓴다 (userId와 동시 지정 불가).
   * 회원 생성과 입소자 등록이 한 트랜잭션으로 묶여, 등록이 실패하면 회원도 같이
   * 없던 일이 된다 — 아무에게도 연결되지 않은 회원이 남지 않는다.
   */
  newBgtUser?: { patientCode: string; birthDate: string; name?: string };
  roomId?: string;
  /** voicecheck.users.id — 입소자 단위 1:1 매핑 */
  voiceUserId?: number;
  /**
   * 보이스웰니스 계정을 새로 발급해 연결 (voiceUserId와 동시 지정 불가).
   * 발급이 실패하면 이 요청으로 만든 것이 전부 되돌아간다.
   */
  issueVoiceAccount?: boolean;
  /** 발급할 보이스웰니스 아이디를 직접 지정. 비우면 환자번호에서 자동으로 만든다 */
  voiceLoginId?: string;
  type?: ResidentType;
  admittedAt?: string;
}

/** 등록 응답 — 계정을 함께 발급한 경우에만 아이디·임시 비밀번호가 채워진다 */
export interface CreateResidentResult extends ResidentDto {
  voiceLoginId: string | null;
  /** 이 응답에서만 볼 수 있다. 서버에는 해시만 남는다 */
  voiceTemporaryPassword: string | null;
}

export interface UpdateResidentInput {
  /** BGT user.id — null이면 회원 연결 해제 */
  userId?: string | null;
  roomId?: string | null;
  /** voicecheck.users.id — null이면 입소자 보이스 연결 해제 */
  voiceUserId?: number | null;
  type?: ResidentType;
}

export async function listResidents(query: ListResidentsQuery): Promise<PaginatedResidents> {
  const res = await api.get<PaginatedResidents>('/residents', { params: query });
  return res.data;
}

export async function getResident(id: string): Promise<ResidentDto> {
  const res = await api.get<ResidentDto>(`/residents/${id}`);
  return res.data;
}

export async function createResident(input: CreateResidentInput): Promise<CreateResidentResult> {
  const res = await api.post<CreateResidentResult>('/residents', input);
  return res.data;
}

export async function updateResident(id: string, input: UpdateResidentInput): Promise<ResidentDto> {
  const res = await api.put<ResidentDto>(`/residents/${id}`, input);
  return res.data;
}

export async function dischargeResident(id: string): Promise<ResidentDto> {
  const res = await api.post<ResidentDto>(`/residents/${id}/discharge`, {});
  return res.data;
}

export async function listResidentsForDashboard(
  query: ListResidentsDashboardQuery,
): Promise<PaginatedResidentList> {
  const res = await api.get<PaginatedResidentList>('/residents/list', {
    params: query,
  });
  return res.data;
}

export interface UsageMetric {
  used: number;
  total: number;
  rate: number;
}

export interface ResidentsUsageSummary {
  date: string;
  bgt: UsageMetric;
  voiceWellness: UsageMetric | null;
}

export async function getResidentsUsageSummary(): Promise<ResidentsUsageSummary> {
  const res = await api.get<ResidentsUsageSummary>('/residents/usage-summary');
  return res.data;
}
